/**
 * Authentication Service with Security Hardening
 * Implements rate limiting and input validation
 * Following OWASP Authentication Best Practices
 */

import { 
  signInWithEmailAndPassword, 
  signOut, 
  createUserWithEmailAndPassword,
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { withRateLimit, formatRateLimitError } from '../utils/rateLimiter';
import { validateField } from '../utils/validation';

/**
 * Email validation schema
 */
const emailSchema = {
  type: 'string',
  required: true,
  maxLength: 254,
  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  errorMessage: 'Invalid email format'
};

/**
 * Password validation schema
 */
const passwordSchema = {
  type: 'string',
  required: true,
  minLength: 8,
  maxLength: 128,
  errorMessage: 'Password must be 8-128 characters'
};

/**
 * Sign in with email and password
 * Rate limited to prevent brute force attacks
 * 
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<object>} - { success: boolean, user?: object, error?: string }
 */
const loginUserInternal = async (email, password) => {
  try {
    // Check if Firebase is initialized
    if (!auth) {
      return {
        success: false,
        error: 'Firebase authentication is not configured. Please check your environment variables.'
      };
    }
    
    // Validate email format
    const emailValidation = validateField(email, emailSchema, 'Email');
    if (!emailValidation.valid) {
      return {
        success: false,
        error: emailValidation.error
      };
    }
    
    // Validate password format
    const passwordValidation = validateField(password, passwordSchema, 'Password');
    if (!passwordValidation.valid) {
      return {
        success: false,
        error: passwordValidation.error
      };
    }
    
    // Attempt authentication
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get user role from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.data();
    
    // Store user ID for rate limiting
    localStorage.setItem('perfume_stories_user_id', user.uid);
    
    return {
      success: true,
      user: {
        uid: user.uid,
        email: user.email,
        role: userData?.role || 'staff',
        name: userData?.name || 'User',
      }
    };
  } catch (error) {
    // Sanitize error messages to prevent information disclosure
    let errorMessage = 'Authentication failed';
    
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      errorMessage = 'Invalid email or password';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed login attempts. Please try again later.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your connection.';
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

// Apply rate limiting: max 5 login attempts per 15 minutes
export const loginUser = withRateLimit(loginUserInternal, 'login');

/**
 * Sign out
 * Rate limited to prevent abuse
 * 
 * @returns {Promise<object>} - { success: boolean, error?: string }
 */
const logoutUserInternal = async () => {
  try {
    await signOut(auth);
    
    // Clear user ID from storage
    localStorage.removeItem('perfume_stories_user_id');
    
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: 'Failed to sign out. Please try again.'
    };
  }
};

export const logoutUser = withRateLimit(logoutUserInternal, 'logout');

/**
 * Create new user (Admin only)
 * Rate limited to prevent account enumeration
 * 
 * NOTE: This function will temporarily log out the current admin and log them back in
 * This is a Firebase limitation - creating a user automatically logs in as that user
 * The process is made seamless by suppressing auth state changes during the operation
 * 
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {object} userData - Additional user data
 * @param {string} currentUserEmail - Current admin's email (to re-login)
 * @param {string} currentUserPassword - Current admin's password (to re-login)
 * @returns {Promise<object>} - { success: boolean, user?: object, error?: string }
 */
const createUserInternal = async (email, password, userData, currentUserEmail, currentUserPassword) => {
  try {
    // Validate email
    const emailValidation = validateField(email, emailSchema, 'Email');
    if (!emailValidation.valid) {
      return {
        success: false,
        error: emailValidation.error
      };
    }
    
    // Validate password
    const passwordValidation = validateField(password, passwordSchema, 'Password');
    if (!passwordValidation.valid) {
      return {
        success: false,
        error: passwordValidation.error
      };
    }
    
    // Validate user data
    if (userData.name) {
      const nameValidation = validateField(userData.name, {
        type: 'string',
        required: true,
        minLength: 2,
        maxLength: 100,
        pattern: /^[a-zA-Z\s'-]+$/,
        errorMessage: 'Name must be 2-100 characters and contain only letters'
      }, 'Name');
      
      if (!nameValidation.valid) {
        return {
          success: false,
          error: nameValidation.error
        };
      }
    }
    
    // Validate role
    if (userData.role && !['admin', 'staff'].includes(userData.role)) {
      return {
        success: false,
        error: 'Invalid role. Must be admin or staff.'
      };
    }
    
    // Set flag to suppress auth state changes during user creation
    window.__suppressAuthStateChange = true;
    
    try {
      // Create the new user (this will log in as the new user)
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      const newUserUid = newUser.uid;
      const newUserEmail = newUser.email;
      
      // Store user data in Firestore (only allowed fields)
      const sanitizedUserData = {
        email: newUser.email,
        name: userData.name || 'User',
        role: userData.role || 'staff',
        createdAt: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'users', newUser.uid), sanitizedUserData);
      
      // Sign out the newly created user
      await signOut(auth);
      
      // Re-login as the admin if credentials provided
      if (currentUserEmail && currentUserPassword) {
        await signInWithEmailAndPassword(auth, currentUserEmail, currentUserPassword);
      }
      
      // Clear the suppression flag
      window.__suppressAuthStateChange = false;
      
      return {
        success: true,
        user: {
          uid: newUserUid,
          email: newUserEmail,
          ...sanitizedUserData
        }
      };
    } catch (error) {
      // Clear the suppression flag on error
      window.__suppressAuthStateChange = false;
      
      // Log the full error for debugging
      console.error('Firebase Auth Error Details:', {
        code: error.code,
        message: error.message,
        fullError: error
      });
      
      throw error;
    }
  } catch (error) {
    let errorMessage = 'Failed to create user';
    
    // Handle both client SDK and Admin SDK error formats
    const errorCode = error.code || error.error?.message || '';
    
    if (errorCode === 'auth/email-already-in-use' || errorCode === 'EMAIL_EXISTS' || errorCode.includes('EMAIL_EXISTS')) {
      errorMessage = 'Email address is already in use';
    } else if (errorCode === 'auth/weak-password') {
      errorMessage = 'Password is too weak. Use at least 8 characters.';
    } else if (errorCode === 'auth/invalid-email') {
      errorMessage = 'Invalid email address';
    } else if (errorCode === 'auth/wrong-password') {
      errorMessage = 'Incorrect admin password. Please try again.';
    } else if (error.message) {
      // Include the actual error message for debugging
      console.error('User creation error:', error);
      errorMessage = `Failed to create user: ${error.message}`;
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

export const createUser = withRateLimit(createUserInternal, 'create_user');

/**
 * Get current user
 * No rate limiting needed for read-only operation
 * 
 * @returns {object|null} - Current user or null
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Listen to auth state changes
 * 
 * @param {function} callback - Callback function
 * @returns {function} - Unsubscribe function
 */
export const onAuthChange = (callback) => {
  if (!auth) {
    console.error('Firebase authentication is not configured. Please check your .env.local file and ensure all required environment variables are set. See .env.example for reference.');
    return () => {}; // Return no-op unsubscribe function
  }
  return onAuthStateChanged(auth, callback);
};

/**
 * Get user role
 * Rate limited to prevent enumeration attacks
 * 
 * @param {string} uid - User ID
 * @returns {Promise<string>} - User role
 */
const getUserRoleInternal = async (uid) => {
  try {
    // Validate UID format
    if (!uid || typeof uid !== 'string' || uid.length > 128) {
      return 'staff';
    }
    
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const role = userDoc.data().role;
      // Ensure role is one of the allowed values
      return ['admin', 'staff'].includes(role) ? role : 'staff';
    }
    return 'staff';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'staff';
  }
};

export const getUserRole = withRateLimit(getUserRoleInternal, 'get_user_role');

/**
 * Export rate limit error formatter for UI
 */
export { formatRateLimitError };
